import { Permission, User } from '../types';
import { NavKey } from './permissions';

export const PERMISSION_MODULES: {
  module: Permission['module'];
  label: string;
  hint?: string;
}[] = [
  { module: 'bookings', label: 'الحجوزات', hint: 'إنشاء وتعديل الحجوزات' },
  {
    module: 'operations',
    label: 'العمليات',
    hint: 'الرحلات؛ وإدارة الباصات/السكن تحتاج إنشاء أو تعديل',
  },
  {
    module: 'boarding',
    label: 'بوابة الصعود',
    hint: 'مسح QR وتسجيل حضور الركاب',
  },
  {
    module: 'housing',
    label: 'التسكين',
    hint: 'أرقام الغرف وموقع الفندق وإرسال واتساب',
  },
  { module: 'accounting', label: 'المحاسبة', hint: 'إغلاق الكاشير والعمليات المالية' },
  { module: 'reports', label: 'التقارير' },
  { module: 'users', label: 'المستخدمين', hint: 'إدارة حسابات الموظفين' },
  { module: 'settings', label: 'الإعدادات' },
];

export const PERMISSION_ACTIONS: {
  action: Permission['actions'][number];
  label: string;
}[] = [
  { action: 'read', label: 'عرض' },
  { action: 'create', label: 'إنشاء' },
  { action: 'update', label: 'تعديل' },
  { action: 'delete', label: 'حذف' },
  { action: 'export', label: 'تصدير' },
];

/** Mirrors backend User.getDefaultPermissions */
export function getDefaultPermissionsForRole(role: string): Permission[] {
  const byRole: Record<string, Permission[]> = {
    super_admin: [
      { module: 'users', actions: ['create', 'read', 'update', 'delete'] },
      { module: 'bookings', actions: ['create', 'read', 'update', 'delete', 'export'] },
      { module: 'operations', actions: ['create', 'read', 'update', 'delete', 'export'] },
      { module: 'boarding', actions: ['create', 'read', 'update', 'delete', 'export'] },
      { module: 'housing', actions: ['create', 'read', 'update', 'delete', 'export'] },
      { module: 'accounting', actions: ['create', 'read', 'update', 'delete', 'export'] },
      { module: 'reports', actions: ['read', 'export'] },
      { module: 'settings', actions: ['read', 'update'] },
    ],
    admin: [
      { module: 'users', actions: ['create', 'read', 'update', 'delete'] },
      { module: 'bookings', actions: ['create', 'read', 'update', 'delete', 'export'] },
      { module: 'operations', actions: ['create', 'read', 'update', 'delete', 'export'] },
      { module: 'boarding', actions: ['create', 'read', 'update', 'delete', 'export'] },
      { module: 'housing', actions: ['create', 'read', 'update', 'delete', 'export'] },
      { module: 'accounting', actions: ['create', 'read', 'update', 'delete', 'export'] },
      { module: 'reports', actions: ['read', 'export'] },
      { module: 'settings', actions: ['read', 'update'] },
    ],
    operations: [
      { module: 'boarding', actions: ['read', 'update'] },
    ],
    hotel_rep: [
      { module: 'housing', actions: ['read', 'update'] },
    ],
    accounting: [
      { module: 'bookings', actions: ['read'] },
      { module: 'accounting', actions: ['create', 'read', 'update'] },
      { module: 'reports', actions: ['read', 'export'] },
    ],
  };
  return (byRole[role] || []).map((p) => ({
    module: p.module,
    actions: [...p.actions],
  }));
}

export function clonePermissions(permissions: Permission[] = []): Permission[] {
  return permissions.map((p) => ({
    module: p.module,
    actions: [...(p.actions || [])],
  }));
}

export function hasAction(
  permissions: Permission[],
  module: Permission['module'],
  action: Permission['actions'][number]
): boolean {
  return permissions.some(
    (p) => p.module === module && (p.actions || []).includes(action)
  );
}

export function togglePermissionAction(
  permissions: Permission[],
  module: Permission['module'],
  action: Permission['actions'][number],
  enabled: boolean
): Permission[] {
  const next = clonePermissions(permissions);
  const idx = next.findIndex((p) => p.module === module);

  if (enabled) {
    if (idx === -1) {
      const actions: Permission['actions'][number][] =
        action === 'read' ? ['read'] : ['read', action];
      next.push({ module, actions });
    } else {
      const set = new Set(next[idx].actions);
      set.add(action);
      if (action !== 'read') set.add('read');
      next[idx].actions = Array.from(set) as Permission['actions'];
    }
  } else if (idx !== -1) {
    if (action === 'read') {
      next.splice(idx, 1);
    } else {
      next[idx].actions = next[idx].actions.filter((a) => a !== action);
      if (next[idx].actions.length === 0) next.splice(idx, 1);
    }
  }

  return next;
}

/** Map sidebar items to required module permission */
const NAV_MODULE: Partial<Record<NavKey, Permission['module']>> = {
  bookings: 'bookings',
  trips: 'operations',
  boarding: 'boarding',
  housing: 'housing',
  buses: 'operations',
  accommodations: 'operations',
  'cash-closing': 'accounting',
  reports: 'reports',
  users: 'users',
  settings: 'settings',
};

export function canSeeNavByPermissions(
  user: User | null | undefined,
  key: NavKey
): boolean {
  if (!user) return false;
  if (user.role === 'super_admin' || user.role === 'admin') return true;

  const perms = user.permissions || [];

  if (key === 'dashboard') {
    // Everyone authenticated gets a home dashboard
    return true;
  }

  // Boarding page: dedicated module (legacy ops read still unlocks)
  if (key === 'boarding') {
    return (
      hasAction(perms, 'boarding', 'read') || hasAction(perms, 'operations', 'read')
    );
  }

  // Management screens — not for boarding-only staff
  if (key === 'trips' || key === 'buses' || key === 'accommodations') {
    return (
      hasAction(perms, 'operations', 'read') ||
      hasAction(perms, 'operations', 'create') ||
      hasAction(perms, 'operations', 'update') ||
      hasAction(perms, 'operations', 'delete')
    );
  }

  const module = NAV_MODULE[key];
  if (!module) return false;
  return hasAction(perms, module, 'read');
}

export function getHomePathForUser(user: User | null | undefined, isVendor = true): string {
  // Everyone lands on their personal dashboard; content is permission-based
  if (isVendor) return '/vendor';
  return '/';
}
